import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import {
  Play,
  Pause,
  Volume2,
  Volume1,
  VolumeX,
  Maximize,
  Minimize,
  RotateCcw,
  RotateCw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './VideoRenderer.module.css';

const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const formattedMins = mins < 10 ? `0${mins}` : mins;
  const formattedSecs = secs < 10 ? `0${secs}` : secs;
  return `${formattedMins}:${formattedSecs}`;
};

const VideoRenderer = ({ contentDetails, viewerUrl }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const timelineRef = useRef(null);
  const idleTimerRef = useRef(null);
  const isSeekingRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadedFraction, setLoadedFraction] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [hoverTime, setHoverTime] = useState(0);
  const [centerRipple, setCenterRipple] = useState(null);

  const fileName =
    contentDetails?.metadata?.file_metadata?.name ||
    contentDetails?.title ||
    'Video Lesson';

  const videoSrc = (() => {
    if (!viewerUrl) return '';
    if (viewerUrl.startsWith('http://') || viewerUrl.startsWith('https://')) {
      return viewerUrl;
    }
    const baseUrl =
      process.env.REACT_APP_SERVER_URL || 'http://localhost:5000/api/v1';
    return `${baseUrl}${viewerUrl.startsWith('/') ? '' : '/'}${viewerUrl}`;
  })();

  // Catch & suppress benign browser play/pause race condition errors
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (
        event?.reason?.name === 'AbortError' ||
        (typeof event?.reason?.message === 'string' &&
          event.reason.message.includes('interrupted by a call to pause'))
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  // Autohide controls during playback
  const wakeControls = useCallback(() => {
    setShowControls(true);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (isPlaying && !isSeekingRef.current) {
      idleTimerRef.current = setTimeout(() => {
        setShowControls(false);
        setShowSpeedMenu(false);
      }, 3000);
    }
  }, [isPlaying]);

  const triggerCenterRipple = (type) => {
    setCenterRipple(type);
    setTimeout(() => setCenterRipple(null), 500);
  };

  const playPromiseRef = useRef(null);

  const safePlay = useCallback(() => {
    const el = playerRef.current;
    if (!el || typeof el.play !== 'function') return;

    const playPromise = el.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromiseRef.current = playPromise;
      playPromise
        .then(() => {
          if (playPromiseRef.current === playPromise)
            playPromiseRef.current = null;
        })
        .catch((err) => {
          if (playPromiseRef.current === playPromise)
            playPromiseRef.current = null;
          if (err?.name !== 'AbortError') {
            console.error('Play failed:', err);
          }
        });
    }
  }, []);

  const safePause = useCallback(() => {
    const el = playerRef.current;
    if (!el || typeof el.pause !== 'function') return;

    if (playPromiseRef.current) {
      // Wait for the pending play() to settle before pausing
      playPromiseRef.current.finally(() => el.pause()).catch(() => {});
    } else {
      el.pause();
    }
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => {
      const next = !prev;
      triggerCenterRipple(next ? 'play' : 'pause');
      if (next) {
        safePlay();
      } else {
        safePause();
      }
      return next;
    });
  }, [safePlay, safePause]);

  // Safe seek mechanism compatible with ReactPlayer v3 (HTMLMediaElement interface)
  const seekTo = (time) => {
    if (!playerRef.current) return;
    if (typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(time, 'seconds');
    }
    if ('currentTime' in playerRef.current) {
      playerRef.current.currentTime = time;
    }
  };

  // v3 callback: onTimeUpdate
  const handleTimeUpdate = (e) => {
    if (isSeekingRef.current) return;
    let time = 0;
    if (typeof e === 'number') {
      time = e;
    } else if (e?.target?.currentTime !== undefined) {
      time = e.target.currentTime;
    } else if (e?.currentTime !== undefined) {
      time = e.currentTime;
    } else if (playerRef.current?.currentTime !== undefined) {
      time = playerRef.current.currentTime;
    }
    if (!isNaN(time)) {
      setCurrentTime(time);
    }
  };

  // v3 callback: onDurationChange
  const handleDurationChange = (e) => {
    let dur = 0;
    if (typeof e === 'number') {
      dur = e;
    } else if (e?.target?.duration !== undefined) {
      dur = e.target.duration;
    } else if (e?.duration !== undefined) {
      dur = e.duration;
    } else if (playerRef.current?.duration !== undefined) {
      dur = playerRef.current.duration;
    }
    if (!isNaN(dur) && dur > 0) {
      setDuration(dur);
    }
  };

  const handleProgress = (e) => {
    if (e?.target?.buffered && e?.target?.duration) {
      const buffered = e.target.buffered;
      if (buffered.length > 0) {
        setLoadedFraction(
          buffered.end(buffered.length - 1) / e.target.duration,
        );
      }
    } else if (typeof e?.loaded === 'number') {
      setLoadedFraction(e.loaded);
    }
  };

  // Timeline scrub handling
  const calculateSeekTime = (e) => {
    if (!timelineRef.current || !duration) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pos * duration;
  };

  const handleTimelineMouseDown = (e) => {
    isSeekingRef.current = true;
    const seekTime = calculateSeekTime(e);
    setCurrentTime(seekTime);
    seekTo(seekTime);
  };

  const handleTimelineMouseMove = (e) => {
    if (!timelineRef.current || !duration) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHoverPosition(pos * 100);
    setHoverTime(pos * duration);

    if (isSeekingRef.current) {
      const seekTime = pos * duration;
      setCurrentTime(seekTime);
      seekTo(seekTime);
    }
  };

  const handleTimelineMouseLeave = () => {
    setHoverPosition(null);
    if (isSeekingRef.current) {
      isSeekingRef.current = false;
    }
  };

  const handleTimelineMouseUp = () => {
    if (isSeekingRef.current) {
      isSeekingRef.current = false;
    }
  };

  const handleVolumeChange = (e) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    setIsMuted(newVol === 0);
  };

  const toggleMute = useCallback(() => {
    if (isMuted) {
      setIsMuted(false);
      if (volume === 0) setVolume(0.5);
    } else {
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  // Skip controls (10s back / forward)
  const skipTime = useCallback(
    (seconds) => {
      const current = currentTime || 0;
      const maxLimit = duration > 0 ? duration : Infinity;
      const target = Math.min(Math.max(0, current + seconds), maxLimit);
      seekTo(target);
      setCurrentTime(target);
    },
    [currentTime, duration],
  );

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error('Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error('Exit fullscreen failed:', err);
      });
    }
  }, []);

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    setShowSpeedMenu(false);
  };

  // Fullscreen sync
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['input', 'textarea'].includes(e.target.tagName?.toLowerCase()))
        return;

      if (e.code === 'Space' || e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        toggleMute();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        skipTime(10);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        skipTime(-10);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, toggleFullscreen, toggleMute, skipTime]);

  const playedPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = loadedFraction * 100;

  return (
    <div className={styles.stageContainer}>
      <div
        ref={containerRef}
        className={`${styles.playerCard} ${isFullscreen ? styles.fullscreen : ''}`}
        onMouseMove={wakeControls}
        onMouseLeave={() => isPlaying && setShowControls(false)}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Subtle Ambient Vignette */}
        <div className={styles.ambientVignette} />

        {/* Top Floating Glass Header */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              className={styles.headerBar}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className={styles.headerInfo}>
                <span className={styles.videoBadge}>VIDEO</span>
                <h2 className={styles.videoTitle}>{fileName}</h2>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Canvas Layer */}
        <div
          className={styles.canvasSurface}
          onClick={togglePlay}
          onDoubleClick={toggleFullscreen}
        >
          {/* ReactPlayer v3 implementation */}
          <ReactPlayer
            ref={playerRef}
            src={videoSrc}
            // playing={isPlaying}
            volume={isMuted ? 0 : volume}
            muted={isMuted}
            playbackRate={playbackSpeed}
            controls={false}
            playsInline={true}
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%' }}
            className={styles.reactPlayer}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
            onDurationChange={handleDurationChange}
            onProgress={handleProgress}
            onWaiting={() => setIsBuffering(true)}
            onPlaying={() => setIsBuffering(false)}
            onEnded={() => setIsPlaying(false)}
            onError={(err) => {
              if (
                err?.name === 'AbortError' ||
                (typeof err?.message === 'string' &&
                  err.message.includes('interrupted by a call to pause'))
              ) {
                return;
              }
              console.error('Playback error:', err);
            }}
          />

          {/* Central Animated State Feedback */}
          <AnimatePresence>
            {isBuffering && (
              <motion.div
                className={styles.centerFeedback}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <div className={styles.spinnerRing} />
              </motion.div>
            )}

            {!isPlaying && !isBuffering && (
              <motion.div
                className={styles.centerFeedback}
                initial={{ opacity: 0, scale: 0.85, transform: 'translate(-50%, -50%)' }}
                animate={{ opacity: 1, scale: 1, transform: 'translate(-50%, -50%)' }}
                exit={{ opacity: 0, scale: 0.85, transform: 'translate(-50%, -50%)' }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className={styles.playSculptButton}>
                  <Play
                    size={26}
                    fill="white"
                    className={styles.playIconOffset}
                  />
                </div>
              </motion.div>
            )}

            {centerRipple && (
              <motion.div
                className={styles.rippleBadge}
                initial={{ opacity: 0.85, scale: 0.8, transform: 'translate(-50%, -50%)' }}
                animate={{ opacity: 0, scale: 1.35, transform: 'translate(-50%, -50%)' }}
                exit={{ opacity: 0, transform: 'translate(-50%, -50%)' }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              >
                {centerRipple === 'play' ? (
                  <Play size={32} fill="white" />
                ) : (
                  <Pause size={32} fill="white" />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom Floating Control Dock */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              className={styles.dockContainer}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 14 }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* High-Precision Interactive Scrubber */}
              <div
                ref={timelineRef}
                className={styles.timelineScrubber}
                onMouseDown={handleTimelineMouseDown}
                onMouseMove={handleTimelineMouseMove}
                onMouseLeave={handleTimelineMouseLeave}
                onMouseUp={handleTimelineMouseUp}
              >
                {/* Background Track */}
                <div className={styles.trackBase}>
                  {/* Loaded Buffer Bar */}
                  <div
                    className={styles.trackBuffered}
                    style={{ width: `${bufferedPercent}%` }}
                  />
                  {/* Played Progress Bar */}
                  <div
                    className={styles.trackPlayed}
                    style={{ width: `${playedPercent}%` }}
                  />
                </div>

                {/* Scrubber Playhead Thumb */}
                <div
                  className={styles.scrubberThumb}
                  style={{ left: `${playedPercent}%` }}
                />

                {/* Cursor Hover Timestamp Tooltip */}
                {hoverPosition !== null && (
                  <div
                    className={styles.hoverTooltip}
                    style={{ left: `${hoverPosition}%` }}
                  >
                    <span>{formatTime(hoverTime)}</span>
                  </div>
                )}
              </div>

              {/* Bottom Utility Controls Row */}
              <div className={styles.dockRow}>
                {/* Left Cluster: Play, Skips, Volume, Timer */}
                <div className={styles.leftCluster}>
                  <motion.button
                    type="button"
                    onClick={togglePlay}
                    className={styles.primaryActionBtn}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                  >
                    {isPlaying ? (
                      <Pause size={17} fill="currentColor" />
                    ) : (
                      <Play
                        size={17}
                        fill="currentColor"
                        className={styles.playIconTinyOffset}
                      />
                    )}
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={() => skipTime(-10)}
                    className={styles.utilityBtn}
                    aria-label="Skip backward 10 seconds"
                    title="Rewind 10s (←)"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                  >
                    <RotateCcw size={16} />
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={() => skipTime(10)}
                    className={styles.utilityBtn}
                    aria-label="Skip forward 10 seconds"
                    title="Forward 10s (→)"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                  >
                    <RotateCw size={16} />
                  </motion.button>

                  {/* Volume Control */}
                  <div className={styles.volumeCluster}>
                    <motion.button
                      type="button"
                      onClick={toggleMute}
                      className={styles.utilityBtn}
                      aria-label={isMuted ? 'Unmute' : 'Mute'}
                      title="Mute (M)"
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.92 }}
                    >
                      {isMuted || volume === 0 ? (
                        <VolumeX size={17} />
                      ) : volume < 0.5 ? (
                        <Volume1 size={17} />
                      ) : (
                        <Volume2 size={17} />
                      )}
                    </motion.button>

                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.02}
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className={styles.volumeRange}
                      style={{
                        '--vol-level': `${(isMuted ? 0 : volume) * 100}%`,
                      }}
                      aria-label="Volume slider"
                    />
                  </div>

                  {/* Tabular Precision Timestamp */}
                  <div className={styles.timestampDisplay}>
                    <span className={styles.timeCurrent}>
                      {formatTime(currentTime)}
                    </span>
                    <span className={styles.timeSlash}>/</span>
                    <span className={styles.timeTotal}>
                      {formatTime(duration)}
                    </span>
                  </div>
                </div>

                {/* Right Cluster: Speed, Fullscreen */}
                <div className={styles.rightCluster}>
                  {/* Speed Selector Popover */}
                  <div className={styles.speedPopoverAnchor}>
                    <motion.button
                      type="button"
                      onClick={() => setShowSpeedMenu((prev) => !prev)}
                      className={`${styles.speedPill} ${playbackSpeed !== 1 ? styles.speedPillActive : ''}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Playback speed selector"
                    >
                      {playbackSpeed}x
                    </motion.button>

                    <AnimatePresence>
                      {showSpeedMenu && (
                        <motion.div
                          className={styles.speedMenuFlyout}
                          initial={{ opacity: 0, y: 8, scale: 0.94 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.94 }}
                          transition={{
                            duration: 0.16,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                        >
                          {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                            <button
                              key={speed}
                              type="button"
                              onClick={() => handleSpeedChange(speed)}
                              className={`${styles.speedMenuItem} ${
                                playbackSpeed === speed
                                  ? styles.speedMenuItemSelected
                                  : ''
                              }`}
                            >
                              <span>{speed}x</span>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Fullscreen Expand/Collapse */}
                  <motion.button
                    type="button"
                    onClick={toggleFullscreen}
                    className={styles.utilityBtn}
                    aria-label={
                      isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'
                    }
                    title={
                      isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'
                    }
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                  >
                    {isFullscreen ? (
                      <Minimize size={17} />
                    ) : (
                      <Maximize size={17} />
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VideoRenderer;